/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BLOCK_LEVELS, SHAPES, COLORS, shuffleArray, playPlaceSound, playWrongBlockSound, playLevelCompleteSound, speakBlockMission } from "@/utils/blockGameData";
import { Home, Star, Volume2, VolumeX, Trophy, Pause, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import Confetti from "react-confetti";
import { useWindowSize } from "@/hooks/useWindowSize";
import { recordGameWon } from "@/utils/playerProfile";
import axios from "axios";

import { logger } from "@/utils/logger";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// SVG Shape renderer
const ShapeSVG = ({ shapeId, color, size = 60, opacity = 1, glow = false }) => {
  const shape = SHAPES[shapeId];
  if (!shape) return null;
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" style={{ opacity, filter: glow ? `drop-shadow(0 0 8px ${color})` : 'none' }}>
      <path d={shape.path} fill={color} stroke={color} strokeWidth="2" />
    </svg>
  );
};

// Color block renderer
const ColorBlock = ({ colorId, size = 60, opacity = 1, glow = false }) => {
  const c = COLORS[colorId];
  if (!c) return null;
  return (
    <div style={{
      width: size, height: size, borderRadius: 12, background: c.hex, opacity,
      boxShadow: glow ? `0 0 16px ${c.hex}80` : `0 2px 8px ${c.hex}40`,
      border: `3px solid ${c.light}`,
    }} className="flex items-center justify-center">
      <span className="font-fredoka text-gold font-bold text-xs drop-shadow-md">{c.name}</span>
    </div>
  );
};

const BlockGame = ({ startLevel = null, onBack }) => {
  const [phase, setPhase] = useState("playing");
  const [levels, setLevels] = useState([]);
  const [levelIdx, setLevelIdx] = useState(0);
  const [qIdx, setQIdx] = useState(0);
  const [score, setScore] = useState(0);
  const [stars, setStars] = useState(0);
  const [selected, setSelected] = useState(null);
  const [matched, setMatched] = useState([]);
  const [feedback, setFeedback] = useState(null);
  const [soundOn, setSoundOn] = useState(true);
  const [sparkle, setSparkle] = useState(null);

  // Pattern state
  const [patternAnswer, setPatternAnswer] = useState(null);

  // Picture puzzle
  const [puzzlePlaced, setPuzzlePlaced] = useState([]);
  const [puzzlePool, setPuzzlePool] = useState([]);

  // Speed mode
  const [speedTimer, setSpeedTimer] = useState(0);
  const speedRef = useRef(null);

  // Save
  const [saveName, setSaveName] = useState(() => {
    try { const d = localStorage.getItem('alba_game_players'); if (d) { const p = JSON.parse(d); return p[0] || ""; } } catch {} return "";
  });
  const [scoreSaved, setScoreSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  const { width, height } = useWindowSize();

  useEffect(() => {
    if (startLevel !== null) {
      const lvl = BLOCK_LEVELS.find(l => l.id === startLevel);
      setLevels(lvl ? [lvl] : [...BLOCK_LEVELS]);
    } else {
      setLevels([...BLOCK_LEVELS]);
    }
  }, [startLevel]);

  const currentLevel = levels[levelIdx];
  const currentQ = currentLevel?.questions[qIdx];

  // Init question
  const initQuestion = useCallback(() => {
    setSelected(null);
    setMatched([]);
    setFeedback(null);
    setSparkle(null);
    setPatternAnswer(null);
    setPuzzlePlaced([]);

    if (!currentQ || !currentLevel) return;

    if (currentLevel.type === "picture_puzzle") {
      setPuzzlePool(shuffleArray(currentQ.pieces.map((p, i) => ({ ...p, id: i }))));
    }

    if (currentLevel.type === "speed") {
      setSpeedTimer(currentLevel.timePerQuestion || 5);
    }

    if (soundOn) {
      const missionText = currentLevel.type === "match_shape" ? "Cocokkan bentuk ke bayangannya!"
        : currentLevel.type === "match_color" ? "Cocokkan warna yang sama!"
        : currentLevel.type === "pattern" ? "Lanjutkan pola yang benar!"
        : currentLevel.type === "picture_puzzle" ? `Susun gambar ${currentQ.name}!`
        : currentLevel.type === "composite" ? currentQ.hint
        : "Pilih jawaban yang benar!";
      setTimeout(() => speakBlockMission(missionText), 300);
    }
  }, [currentQ, currentLevel, soundOn]);

  useEffect(() => {
    if (currentQ && phase === "playing") initQuestion();
  }, [levelIdx, qIdx, phase, initQuestion]);

  // Speed timer
  useEffect(() => {
    if (phase !== "playing" || !currentLevel || currentLevel.type !== "speed") return;
    if (speedTimer <= 0) {
      // Time up - wrong answer
      if (soundOn) playWrongBlockSound();
      setFeedback({ type: "wrong", text: `Waktu habis! Jawaban: ${currentQ?.target || '?'}` });
      setScore(prev => Math.max(0, prev - 5));
      setTimeout(() => { setFeedback(null); advanceQuestion(); }, 2500);
      return;
    }
    speedRef.current = setTimeout(() => setSpeedTimer(prev => prev - 1), 1000);
    return () => clearTimeout(speedRef.current);
  }, [speedTimer, phase, currentLevel]);

  const advanceQuestion = useCallback(() => {
    if (!currentLevel) return;
    if (qIdx + 1 < currentLevel.questions.length) {
      setQIdx(prev => prev + 1);
    } else {
      if (levelIdx + 1 < levels.length) {
        setPhase("levelUp");
      } else {
        setPhase("allDone");
        recordGameWon("block");
      }
    }
  }, [qIdx, levelIdx, levels, currentLevel]);

  const handleCorrect = useCallback(() => {
    if (soundOn) playPlaceSound();
    setScore(prev => prev + 10);
    setStars(prev => prev + 1);
    setSparkle(Date.now());
    setFeedback({ type: "correct", text: "Benar!" });
    setTimeout(() => { setFeedback(null); setSparkle(null); advanceQuestion(); }, 1200);
  }, [soundOn, advanceQuestion]);

  const handleWrong = useCallback((hint) => {
    if (soundOn) playWrongBlockSound();
    const msg = hint ? `Salah! ${hint}` : "Coba lagi ya!";
    setFeedback({ type: "wrong", text: msg });
    setScore(prev => Math.max(0, prev - 3));
    setTimeout(() => setFeedback(null), 2500);
  }, [soundOn]);

  // ---- MATCH SHAPE ----
  const handleShapeSelect = (shapeId) => {
    if (phase !== "playing") return;
    setSelected(shapeId);
  };

  const handleTargetClick = (targetShape) => {
    if (phase !== "playing" || !selected) return;
    if (selected === targetShape && !matched.includes(targetShape)) {
      setMatched(prev => [...prev, targetShape]);
      setSelected(null);
      if (soundOn) playPlaceSound();
      setSparkle(targetShape);
      setTimeout(() => setSparkle(null), 600);
      const newMatched = [...matched, targetShape];
      if (newMatched.length >= currentQ.targets.length) {
        handleCorrect();
      }
    } else {
      const shapeName = SHAPES[targetShape]?.name || targetShape;
      handleWrong(`Cari bentuk: ${shapeName}`);
      setSelected(null);
    }
  };

  // ---- MATCH COLOR ----
  const handleColorSelect = (colorId) => {
    if (phase !== "playing") return;
    setSelected(colorId);
  };

  const handleColorTargetClick = (targetColor) => {
    if (phase !== "playing" || !selected) return;
    if (selected === targetColor && !matched.includes(targetColor)) {
      setMatched(prev => [...prev, targetColor]);
      setSelected(null);
      if (soundOn) playPlaceSound();
      setSparkle(targetColor);
      setTimeout(() => setSparkle(null), 600);
      const newMatched = [...matched, targetColor];
      if (newMatched.length >= currentQ.targets.length) {
        handleCorrect();
      }
    } else {
      const colorName = COLORS[targetColor]?.name || targetColor;
      handleWrong(`Cari warna: ${colorName}`);
      setSelected(null);
    }
  };

  // ---- PATTERN ----
  const handlePatternSelect = (answer) => {
    if (phase !== "playing") return;
    setPatternAnswer(answer);
    if (answer === currentQ.answer) {
      handleCorrect();
    } else {
      handleWrong(`Jawaban yang benar: ${currentQ.answer}`);
      setTimeout(() => setPatternAnswer(null), 600);
    }
  };

  // ---- PICTURE PUZZLE ----
  const handlePuzzlePieceClick = (piece) => {
    if (phase !== "playing") return;
    setSelected(piece.id);
  };

  const handlePuzzleSlotClick = (row, col) => {
    if (phase !== "playing" || selected === null) return;
    const piece = puzzlePool.find(p => p.id === selected);
    if (!piece) return;
    if (piece.pos[0] === row && piece.pos[1] === col) {
      setPuzzlePlaced(prev => [...prev, piece.id]);
      setPuzzlePool(prev => prev.filter(p => p.id !== piece.id));
      setSelected(null);
      if (soundOn) playPlaceSound();
      setSparkle(`${row}-${col}`);
      setTimeout(() => setSparkle(null), 600);
      const newPlaced = [...puzzlePlaced, piece.id];
      if (newPlaced.length >= currentQ.pieces.length) {
        handleCorrect();
      }
    } else {
      handleWrong("Coba taruh di posisi lain!");
      setSelected(null);
    }
  };

  // ---- COMPOSITE ----
  const handleCompositeAnswer = (isCorrect) => {
    if (phase !== "playing") return;
    if (isCorrect) handleCorrect();
    else handleWrong(`Jawaban: ${currentQ?.correctLabel || "lihat petunjuk"}`);
  };

  // ---- SPEED ----
  const handleSpeedAnswer = (answer) => {
    if (phase !== "playing") return;
    clearTimeout(speedRef.current);
    if (answer === currentQ.target) {
      handleCorrect();
    } else {
      handleWrong(`Jawaban: ${currentQ.target}`);
      setTimeout(() => advanceQuestion(), 2500);
    }
  };

  // Next level
  const goNextLevel = () => {
    setLevelIdx(prev => prev + 1);
    setQIdx(0);
    setPhase("playing");
  };

  // Restart
  const restartGame = () => {
    setLevelIdx(0); setQIdx(0); setScore(0); setStars(0);
    setMatched([]); setSelected(null); setSaveName(""); setScoreSaved(false);
    setPhase("playing");
  };

  // Save score
  const handleSaveScore = async () => {
    if (!saveName.trim() || saving) return;
    setSaving(true);
    try {
      await axios.post(`${API}/game/record`, {
        player_name: saveName.trim(), mode: "block", duration_seconds: 0,
        score, stars, level: currentLevel?.name || "All", opponent_type: "solo",
      });
      setScoreSaved(true);
    } catch (e) { logger.error(e); }
    setSaving(false);
  };

  if (!currentLevel && phase === "playing") return null;

  const lvlColor = currentLevel?.color || "#3B82F6";

  // ========== RENDER FUNCTIONS ==========
  const renderMatchShape = () => {
    if (!currentQ) return null;
    const pool = shuffleArray(currentQ.pool);
    return (
      <div className="flex flex-col items-center gap-6">
        <p className="font-fredoka text-gold-light/80 text-sm">Pilih bentuk, lalu taruh ke bayangan yang cocok!</p>
        {/* Targets (shadows) */}
        <div className="flex flex-wrap gap-4 justify-center">
          {currentQ.targets.map((t) => (
            <motion.button key={t} data-testid={`block-target-${t}`}
              onClick={() => handleTargetClick(t)}
              whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
              className="relative rounded-xl p-3 border-2 transition-all"
              style={{
                borderColor: matched.includes(t) ? '#4ade80' : selected ? `${lvlColor}` : `${lvlColor}40`,
                background: matched.includes(t) ? '#4ade8015' : `${lvlColor}08`,
                boxShadow: sparkle === t ? `0 0 20px #4ade8080` : 'none',
              }}>
              <ShapeSVG shapeId={t} color={matched.includes(t) ? '#4ade80' : `${lvlColor}30`} size={90} />
              {matched.includes(t) && (
                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="absolute inset-0 flex items-center justify-center">
                  <ShapeSVG shapeId={t} color="#4ade80" size={90} glow />
                </motion.div>
              )}
            </motion.button>
          ))}
        </div>
        <div className="w-full h-px bg-brown-border my-1" />
        {/* Pool (draggable shapes) */}
        <div className="flex flex-wrap gap-3 justify-center">
          {pool.filter(s => !matched.includes(s)).map((s, i) => (
            <motion.button key={`${s}-${i}`} data-testid={`block-pool-${s}-${i}`}
              onClick={() => handleShapeSelect(s)}
              whileHover={{ scale: 1.1, y: -4 }} whileTap={{ scale: 0.9 }}
              className="rounded-xl p-2 border-2 transition-all"
              style={{
                borderColor: selected === s ? '#FFD700' : `${lvlColor}30`,
                background: selected === s ? '#FFD70015' : 'transparent',
                boxShadow: selected === s ? '0 0 16px #FFD70040' : 'none',
              }}>
              <ShapeSVG shapeId={s} color={COLORS[Object.keys(COLORS)[i % 8]]?.hex || lvlColor} size={75} />
              <p className="font-fredoka text-xs text-gold-light/80/70 mt-1">{SHAPES[s]?.name}</p>
            </motion.button>
          ))}
        </div>
      </div>
    );
  };

  const renderMatchColor = () => {
    if (!currentQ) return null;
    const pool = shuffleArray(currentQ.pool);
    return (
      <div className="flex flex-col items-center gap-6">
        <p className="font-fredoka text-gold-light/80 text-sm">Pilih warna, lalu taruh ke slot yang cocok!</p>
        {/* Target slots */}
        <div className="flex flex-wrap gap-4 justify-center">
          {currentQ.targets.map((t) => (
            <motion.button key={t} data-testid={`block-color-target-${t}`}
              onClick={() => handleColorTargetClick(t)}
              whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
              className="rounded-xl p-2 border-3 transition-all"
              style={{
                borderColor: matched.includes(t) ? '#4ade80' : COLORS[t]?.hex || '#666',
                borderWidth: 3, borderStyle: 'dashed',
                background: matched.includes(t) ? `${COLORS[t]?.hex}40` : `${COLORS[t]?.hex}10`,
                boxShadow: sparkle === t ? `0 0 20px ${COLORS[t]?.hex}80` : 'none',
                width: 100, height: 100,
              }}>
              {matched.includes(t) ? (
                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}>
                  <ColorBlock colorId={t} size={80} glow />
                </motion.div>
              ) : (
                <span className="font-fredoka text-xs font-bold" style={{ color: COLORS[t]?.hex }}>{COLORS[t]?.name}</span>
              )}
            </motion.button>
          ))}
        </div>
        <div className="w-full h-px bg-brown-border my-1" />
        {/* Pool */}
        <div className="flex flex-wrap gap-3 justify-center">
          {pool.filter(c => !matched.includes(c)).map((c, i) => (
            <motion.button key={`${c}-${i}`} data-testid={`block-color-pool-${c}-${i}`}
              onClick={() => handleColorSelect(c)}
              whileHover={{ scale: 1.1, y: -4 }} whileTap={{ scale: 0.9 }}
              className="rounded-xl border-2 transition-all"
              style={{
                borderColor: selected === c ? '#FFD700' : 'transparent',
                boxShadow: selected === c ? '0 0 16px #FFD70040' : 'none',
              }}>
              <ColorBlock colorId={c} size={65} />
            </motion.button>
          ))}
        </div>
      </div>
    );
  };

  const renderPattern = () => {
    if (!currentQ) return null;
    const isColorPattern = Object.keys(COLORS).includes(currentQ.pattern[0]);
    return (
      <div className="flex flex-col items-center gap-5">
        <p className="font-fredoka text-gold-light/80 text-sm">Apa yang selanjutnya?</p>
        {/* Pattern display */}
        <div className="flex gap-2 items-center flex-wrap justify-center">
          {currentQ.pattern.map((item, i) => (
            <motion.div key={i} initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: i * 0.1 }}
              className="rounded-lg border border-gold/20 p-1">
              {isColorPattern
                ? <div style={{ width: 48, height: 48, borderRadius: 8, background: COLORS[item]?.hex }} />
                : <ShapeSVG shapeId={item} color={lvlColor} size={48} />
              }
            </motion.div>
          ))}
          <motion.div animate={{ scale: [1, 1.15, 1] }} transition={{ duration: 1, repeat: Infinity }}
            className="rounded-lg border-2 border-dashed p-1 flex items-center justify-center"
            style={{ borderColor: '#FFD700', width: 58, height: 58 }}>
            <span className="font-fredoka text-white text-lg">?</span>
          </motion.div>
        </div>
        {/* Options */}
        <div className="flex gap-3 flex-wrap justify-center">
          {shuffleArray(currentQ.options).map((opt, i) => (
            <motion.button key={`${opt}-${i}`} data-testid={`block-pattern-${opt}`}
              onClick={() => handlePatternSelect(opt)}
              whileHover={{ scale: 1.1, y: -4 }} whileTap={{ scale: 0.9 }}
              className="rounded-xl p-2 border-2 transition-all"
              style={{
                borderColor: patternAnswer === opt
                  ? (opt === currentQ.answer ? '#4ade80' : '#EF4444')
                  : `${lvlColor}30`,
                background: patternAnswer === opt
                  ? (opt === currentQ.answer ? '#4ade8015' : '#EF444415')
                  : 'transparent',
              }}>
              {isColorPattern
                ? <div style={{ width: 56, height: 56, borderRadius: 10, background: COLORS[opt]?.hex }} />
                : <ShapeSVG shapeId={opt} color={lvlColor} size={56} />
              }
            </motion.button>
          ))}
        </div>
      </div>
    );
  };

  const renderPicturePuzzle = () => {
    if (!currentQ) return null;
    const gs = currentQ.gridSize;
    return (
      <div className="flex flex-col items-center gap-5">
        <p className="font-fredoka text-gold-light/80 text-sm">Susun gambar <span className="text-gold font-bold">{currentQ.name}</span>!</p>
        {/* Grid */}
        <div className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(${gs}, 1fr)` }}>
          {Array.from({ length: gs * gs }).map((_, idx) => {
            const r = Math.floor(idx / gs);
            const c = idx % gs;
            const placed = currentQ.pieces.find(p => p.pos[0] === r && p.pos[1] === c && puzzlePlaced.includes(p.id || currentQ.pieces.indexOf(p)));
            const placedId = currentQ.pieces.findIndex(p => p.pos[0] === r && p.pos[1] === c);
            const isPlaced = puzzlePlaced.includes(placedId);
            const piece = currentQ.pieces.find(p => p.pos[0] === r && p.pos[1] === c);
            return (
              <motion.button key={`${r}-${c}`} data-testid={`block-puzzle-slot-${r}-${c}`}
                onClick={() => handlePuzzleSlotClick(r, c)}
                whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                className="rounded-lg border-2 flex items-center justify-center transition-all"
                style={{
                  width: 90, height: 90,
                  borderColor: isPlaced ? '#4ade80' : selected !== null ? '#FFD700' : `${lvlColor}30`,
                  borderStyle: isPlaced ? 'solid' : 'dashed',
                  background: isPlaced ? `${piece?.color}60` : `${piece?.color}10`,
                  boxShadow: sparkle === `${r}-${c}` ? `0 0 16px #4ade8080` : 'none',
                }}>
                {isPlaced ? (
                  <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} className="text-2xl">{piece?.label}</motion.span>
                ) : null}
              </motion.button>
            );
          })}
        </div>
        {/* Pool */}
        <div className="flex flex-wrap gap-2 justify-center mt-2">
          {puzzlePool.map((p) => (
            <motion.button key={p.id} data-testid={`block-puzzle-piece-${p.id}`}
              onClick={() => handlePuzzlePieceClick(p)}
              whileHover={{ scale: 1.1, y: -3 }} whileTap={{ scale: 0.9 }}
              className="rounded-lg border-2 flex items-center justify-center transition-all"
              style={{
                width: 75, height: 75, background: `${p.color}80`,
                borderColor: selected === p.id ? '#FFD700' : `${p.color}`,
                boxShadow: selected === p.id ? '0 0 12px #FFD70060' : 'none',
              }}>
              <span className="text-xl">{p.label}</span>
            </motion.button>
          ))}
        </div>
      </div>
    );
  };

  const renderComposite = () => {
    if (!currentQ) return null;
    return (
      <div className="flex flex-col items-center gap-5">
        <p className="font-fredoka text-gold-light/80 text-sm">{currentQ.hint}</p>
        {/* Target shape */}
        <div className="rounded-xl border-2 border-dashed p-4" style={{ borderColor: `${lvlColor}60` }}>
          <ShapeSVG shapeId={currentQ.target} color={`${lvlColor}30`} size={100} />
          <p className="font-fredoka text-center text-sm mt-2" style={{ color: lvlColor }}>{SHAPES[currentQ.target]?.name}</p>
        </div>
        {/* Parts */}
        <div className="flex gap-2 flex-wrap justify-center">
          {currentQ.parts.map((p, i) => (
            <div key={i} className="rounded-lg border border-gold/20 p-2">
              <ShapeSVG shapeId={p} color={lvlColor} size={40} />
            </div>
          ))}
        </div>
        <p className="font-fredoka text-gold-light/80 text-xs">Apakah potongan di atas bisa membentuk {SHAPES[currentQ.target]?.name}?</p>
        <div className="flex gap-3">
          <Button data-testid="block-composite-yes" onClick={() => handleCompositeAnswer(true)}
            className="px-8 py-3 bg-green-500 hover:bg-green-600 text-gold font-fredoka font-bold text-lg">
            Ya, Benar!
          </Button>
          <Button data-testid="block-composite-no" onClick={() => handleCompositeAnswer(false)}
            className="px-8 py-3 bg-red-500 hover:bg-red-600 text-gold font-fredoka font-bold text-lg">
            Bukan
          </Button>
        </div>
      </div>
    );
  };

  const renderSpeed = () => {
    if (!currentQ) return null;
    const isColor = currentQ.type === "color";
    const targetLabel = isColor ? COLORS[currentQ.target]?.name : SHAPES[currentQ.target]?.name;
    return (
      <div className="flex flex-col items-center gap-5">
        {/* Timer */}
        <div className="flex items-center gap-3">
          <div className="w-32 h-3 rounded-full glass-card overflow-hidden">
            <motion.div className="h-full rounded-full" animate={{ width: `${(speedTimer / (currentLevel?.timePerQuestion || 5)) * 100}%` }}
              style={{ background: speedTimer > 2 ? '#4ade80' : '#EF4444' }} />
          </div>
          <span className="font-fredoka text-sm" style={{ color: speedTimer > 2 ? '#4ade80' : '#EF4444' }}>{speedTimer}s</span>
        </div>
        <p className="font-fredoka text-sm text-gold-light/80">
          Cari <span className="font-bold text-white">{targetLabel}</span>!
        </p>
        {/* Target display */}
        <div className="rounded-xl border-2 border-dashed p-3" style={{ borderColor: '#FFD700' }}>
          {isColor
            ? <ColorBlock colorId={currentQ.target} size={80} />
            : <ShapeSVG shapeId={currentQ.target} color={lvlColor} size={80} />
          }
        </div>
        {/* Options */}
        <div className="flex gap-3 flex-wrap justify-center">
          {shuffleArray(currentQ.options).map((opt, i) => (
            <motion.button key={`${opt}-${i}`} data-testid={`block-speed-${opt}`}
              onClick={() => handleSpeedAnswer(opt)}
              whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
              className="rounded-xl p-3 border-2 transition-all"
              style={{ borderColor: `${lvlColor}30` }}>
              {isColor
                ? <ColorBlock colorId={opt} size={60} />
                : <ShapeSVG shapeId={opt} color={COLORS[Object.keys(COLORS)[i % 8]]?.hex || lvlColor} size={60} />
              }
            </motion.button>
          ))}
        </div>
      </div>
    );
  };

  const renderGameContent = () => {
    if (!currentLevel) return null;
    switch (currentLevel.type) {
      case "match_shape": return renderMatchShape();
      case "match_color": return renderMatchColor();
      case "pattern": return renderPattern();
      case "picture_puzzle": return renderPicturePuzzle();
      case "composite": return renderComposite();
      case "speed": return renderSpeed();
      default: return null;
    }
  };

  return (
    <div className="flex flex-col items-center w-full max-w-5xl mx-auto px-2" data-testid="block-game-container">
      {phase === "allDone" && <Confetti width={width} height={height} colors={["#3B82F6","#EF4444","#22C55E","#EAB308","#A855F7","#F97316"]} recycle={false} numberOfPieces={400} />}

      {/* Top Bar */}
      {phase === "playing" && currentLevel && (
        <motion.div initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="w-full mb-3">
          <div className="glass-card-strong rounded-xl p-3 border border-gold/20">
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center gap-2">
                <span className="text-lg">{currentLevel.icon}</span>
                <span className="font-fredoka text-sm" style={{ color: lvlColor }}>Lv{currentLevel.id} {currentLevel.name}</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1">
                  <Star className="w-4 h-4 text-white fill-gold" />
                  <span className="font-fredoka text-white text-sm" data-testid="block-score">{score}</span>
                </div>
                <Button onClick={() => setSoundOn(!soundOn)} variant="outline" size="sm" className="border-gold/20 text-gold hover:bg-gold/10 h-8 w-8 p-0" data-testid="block-sound-toggle">
                  {soundOn ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
                </Button>
                <Button onClick={onBack} variant="outline" size="sm" className="border-gold/20 text-pink-300 hover:bg-gold/10 h-8 px-2" data-testid="block-back-btn">
                  <Home className="w-3.5 h-3.5 mr-1" />Menu
                </Button>
              </div>
            </div>
            {/* Progress */}
            <div className="flex gap-1 justify-center">
              {currentLevel.questions.map((_, i) => (
                <div key={i} className="w-3 h-1.5 rounded-full" style={{ background: i < qIdx ? '#4ade80' : i === qIdx ? lvlColor : '#3a2a1a' }} />
              ))}
            </div>
          </div>
        </motion.div>
      )}

      {/* Feedback */}
      <AnimatePresence>
        {feedback && phase === "playing" && (
          <motion.div initial={{ opacity: 0, y: -10, scale: 0.8 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }}
            className={`mb-3 px-5 py-1.5 rounded-full font-fredoka text-sm font-bold ${
              feedback.type === "correct" ? "bg-green-500/20 text-green-400 border border-green-500/40" : "bg-red-500/20 text-red-400 border border-red-500/40"
            }`} data-testid="block-feedback">
            {feedback.text}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Game content */}
      {phase === "playing" && (
        <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
          className="glass-card-strong/60 backdrop-blur-md rounded-2xl p-5 sm:p-6 border border-gold/20 w-full"
          style={{ boxShadow: `0 0 30px ${lvlColor}10` }}>
          {renderGameContent()}
        </motion.div>
      )}

      {/* Level Up */}
      <AnimatePresence>
        {phase === "levelUp" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <motion.div initial={{ scale: 0.5 }} animate={{ scale: 1 }} transition={{ type: "spring" }}
              className="rounded-2xl p-6 border-2 text-center max-w-md w-full" style={{ background: 'rgba(30,20,10,0.95)', borderColor: `${lvlColor}44` }}>
              <h2 className="font-fredoka text-xl sm:text-2xl font-bold mb-2" style={{ color: lvlColor }}>Level {currentLevel?.id} Selesai!</h2>
              <p className="font-fredoka text-gold-light/80 text-sm mb-3">{currentLevel?.name}</p>
              <div className="flex justify-center gap-1 mb-4">
                {[1,2,3].map(s => <Star key={s} className={`w-8 h-8 ${s <= Math.min(3, Math.ceil(stars / Math.max(1, qIdx + 1))) ? 'fill-current' : ''}`}
                  style={{ color: s <= Math.min(3, Math.ceil(stars / Math.max(1, qIdx + 1))) ? '#FFD700' : '#3a2a1a' }} />)}
              </div>
              <p className="font-fredoka text-gold-light/80 text-sm mb-4">Skor: <span className="font-bold text-white">{score}</span></p>
              <Button onClick={goNextLevel} className="w-full font-fredoka font-bold text-brown-dark bg-gradient-to-r from-yellow-400 to-amber-500 hover:bg-gradient-to-r from-yellow-400 to-amber-500/90" data-testid="block-next-level-btn">
                Lanjut Level Berikutnya!
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* All Done */}
      <AnimatePresence>
        {phase === "allDone" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <motion.div initial={{ scale: 0, rotate: -180 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: "spring", damping: 15 }}
              className="rounded-2xl p-6 sm:p-8 border-2 text-center max-w-md w-full"
              style={{ background: 'rgba(30,20,10,0.95)', borderColor: '#FFD70044', boxShadow: '0 0 60px rgba(255,215,0,0.2)' }}>
              <Trophy className="w-16 h-16 mx-auto mb-3 text-white" />
              <h2 className="font-fredoka text-2xl sm:text-3xl font-bold text-white mb-2">Semua Level Selesai!</h2>
              <p className="font-fredoka text-gold-light/80 mb-3">Kamu pintar sekali!</p>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="rounded-lg p-3 border border-gold/20/20 glass-card">
                  <p className="font-fredoka text-gold-light/80 text-xs">Skor</p>
                  <p className="font-fredoka text-2xl text-gold font-bold">{score}</p>
                </div>
                <div className="rounded-lg p-3 border border-gold/20/20 glass-card">
                  <p className="font-fredoka text-gold-light/80 text-xs">Bintang</p>
                  <p className="font-fredoka text-2xl text-gold font-bold">{stars}</p>
                </div>
              </div>
              <div className="flex flex-col gap-2">
                {!scoreSaved ? (
                  <div className="flex gap-2 mb-1">
                    <input data-testid="block-save-name-input" type="text" value={saveName}
                      onChange={(e) => setSaveName(e.target.value)} placeholder="Nama kamu..." maxLength={50}
                      className="flex-1 glass-card border border-gold/20 rounded-lg px-3 py-2 text-white font-fredoka text-sm focus:outline-none focus:border-gold/20"
                      onKeyDown={(e) => e.key === 'Enter' && handleSaveScore()} />
                    <Button onClick={handleSaveScore} disabled={!saveName.trim() || saving}
                      className="font-fredoka font-bold text-brown-dark px-4" style={{ background: '#4ade80' }}
                      data-testid="block-save-score-btn">{saving ? '...' : 'Simpan'}</Button>
                  </div>
                ) : <p className="text-green-400 font-fredoka text-sm mb-1" data-testid="block-score-saved">Skor tersimpan!</p>}
                <Button onClick={restartGame} className="w-full font-fredoka font-bold text-brown-dark bg-gradient-to-r from-yellow-400 to-amber-500 hover:bg-gradient-to-r from-yellow-400 to-amber-500/90" data-testid="block-replay-btn">Main Lagi</Button>
                <Button onClick={onBack} variant="outline" className="w-full border-gold/20 text-pink-300 hover:bg-gold/10 font-fredoka" data-testid="block-finish-btn">
                  <Home className="w-4 h-4 mr-2" />Kembali ke Menu
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BlockGame;
